import { Fragment, useMemo, useState } from 'react';
import { Ban, Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import {
  GEOGRAPHIC_REGIONS,
  getRegionForCountry,
} from '../../../config/constants/geographicRegionsData';
import { exchangeShortName } from '../../../config/constants/exchangeShortNames';
import { FilterExcludedCountBadge } from '../FilterExcludedCountBadge';
import { FilterResetButton } from '../FilterResetButton';
import { ClassificationTypeBadge } from '../ClassificationTypeBadge';

const TABS = ['regions', 'countries', 'exchanges'] as const;
type Tab = (typeof TABS)[number];

const TAB_LABELS: Record<Tab, string> = {
  regions: 'Regions',
  countries: 'Countries',
  exchanges: 'Exchanges',
};

interface GeographicClassificationsPanelProps {
  visibleCountries: ReadonlyArray<{ code: string; label: string }>;
  excludedCountries: Set<string>;
  /**
   * Countries the universe holds no constituent for TODAY. Rendered muted, but
   * still SELECTABLE: a backtest is point-in-time, so excluding a country the
   * index dropped (Russia, gone 2022-03-18) is legitimate over a range that
   * covers it. Greying it out silently made that impossible.
   */
  countriesNotInUniverse?: Set<string>;
  /** Countries holding constituents in the universe today. */
  liveCountries?: Set<string>;
  /** MICs holding constituents in the universe today. */
  liveMics?: Set<string>;
  onSetExcluded: (codes: Array<string>) => void;
  visibleExchanges: ReadonlyArray<{
    code: string;
    label: string;
    countryCode: string;
  }>;
  excludedExchanges: Set<string>;
  onToggleExchange: (code: string) => void;
  onSwitchToSimple: () => void;
  onReset?: () => void;
  /** Securities dropped by this filter — same badge as the simple header. */
  excludedSecurityCount?: number;
}

export function GeographicClassificationsPanel({
  visibleCountries,
  excludedCountries,
  countriesNotInUniverse,
  liveCountries,
  liveMics,
  onSetExcluded,
  visibleExchanges,
  excludedExchanges,
  onToggleExchange,
  onSwitchToSimple,
  onReset,
  excludedSecurityCount = 0,
}: GeographicClassificationsPanelProps) {
  const [activeTab, setActiveTab] = useState<Tab>('regions');
  const [search, setSearch] = useState('');

  // Group visible countries by region
  const regionRows = useMemo(() => {
    const countsByRegion = new Map<string, Array<string>>();
    for (const country of visibleCountries) {
      const regionCode = getRegionForCountry(country.code);
      const list = countsByRegion.get(regionCode) ?? [];
      list.push(country.code);
      countsByRegion.set(regionCode, list);
    }
    return GEOGRAPHIC_REGIONS.filter(
      (r) => (countsByRegion.get(r.code)?.length ?? 0) > 0,
    ).map((r) => ({
      code: r.code,
      label: r.label,
      countryCodes: countsByRegion.get(r.code) ?? [],
    }));
  }, [visibleCountries]);

  const filteredRegions = useMemo(() => {
    if (!search) return regionRows;
    const q = search.toLowerCase();
    return regionRows.filter((r) => r.label.toLowerCase().includes(q));
  }, [regionRows, search]);

  const filteredCountries = useMemo(() => {
    if (!search) return visibleCountries;
    const q = search.toLowerCase();
    return visibleCountries.filter(
      (c) =>
        c.label.toLowerCase().includes(q) || c.code.toLowerCase().includes(q),
    );
  }, [visibleCountries, search]);

  const handleRegionToggle = (regionCountryCodes: Array<string>) => {
    const allExcluded = regionCountryCodes.every((c) =>
      excludedCountries.has(c),
    );
    if (allExcluded) {
      // Include: remove region's countries from excluded
      const next = Array.from(excludedCountries).filter(
        (c) => !regionCountryCodes.includes(c),
      );
      onSetExcluded(next);
    } else {
      // Exclude: add region's countries to excluded
      const existing = new Set(excludedCountries);
      for (const c of regionCountryCodes) existing.add(c);
      onSetExcluded(Array.from(existing));
    }
  };

  const handleCountryToggle = (countryCode: string) => {
    const next = new Set(excludedCountries);
    if (next.has(countryCode)) next.delete(countryCode);
    else next.add(countryCode);
    onSetExcluded(Array.from(next));
  };

  const countryLabelByCode = useMemo(
    () => new Map(visibleCountries.map((c) => [c.code, c.label])),
    [visibleCountries],
  );

  const filteredExchanges = useMemo(() => {
    if (!search) return visibleExchanges;
    const q = search.toLowerCase();
    return visibleExchanges.filter(
      (e) =>
        e.label.toLowerCase().includes(q) ||
        exchangeShortName(e.code).toLowerCase().includes(q) ||
        e.code.toLowerCase().includes(q) ||
        (countryLabelByCode.get(e.countryCode) ?? e.countryCode)
          .toLowerCase()
          .includes(q),
    );
  }, [visibleExchanges, search, countryLabelByCode]);

  const tabCounts: Record<Tab, number> = {
    regions: regionRows.length,
    countries: visibleCountries.length,
    exchanges: visibleExchanges.length,
  };

  // While searching, results span ALL categories (regions + countries +
  // exchanges), not just the active tab — matching the Figma's flat search.
  const searching = search.trim().length > 0;
  const noSearchMatches =
    searching &&
    filteredRegions.length === 0 &&
    filteredCountries.length === 0 &&
    filteredExchanges.length === 0;

  // Exchanges live in this panel too (its third tab), so an exchange-only
  // exclusion is still an active filter. Badge and Reset read the SAME flag —
  // they drifted apart once, leaving a panel that reported "487 excluded" with
  // no way to undo it.
  const hasExclusions = excludedCountries.size > 0 || excludedExchanges.size > 0;

  return (
    <div className="space-y-2">
      {/* Header — Reset right of the title (Figma), Simple View toggle right. */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[15px] font-medium text-muted-foreground truncate">
            Regions, Countries, & Exchanges
          </span>
          {hasExclusions && onReset && <FilterResetButton onClick={onReset} />}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {hasExclusions && (
            <FilterExcludedCountBadge count={excludedSecurityCount} />
          )}
          <button
            type="button"
            onClick={onSwitchToSimple}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Simple View
          </button>
        </div>
      </div>

      {/* Panel — Figma caps the height on the scrollable list only, so the
          search header + tabs stay pinned and the list gets the full max-h-72
          of rows (not panel-height-minus-chrome). */}
      <div className="rounded-lg border border-border bg-card overflow-hidden">
        {/* Search — Figma: labeled header on a muted wash, bordered
            Input with the icon nested inside. */}
        <div className="border-b bg-muted/30 px-4 py-3">
          {/* Title and search share ONE row: the search takes the right
              half of the header band. Stacked, the two claimed a header
              twice as tall as it needed to be and pushed the tabs — the
              part people actually reach for — further down the panel. */}
          <div className="flex items-center gap-3">
            <Label className="text-[15px] flex-1 min-w-0 truncate">
              Geographic Classifications
            </Label>
            <div className="relative w-1/2 shrink-0">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search regions, countries, or exchanges..."
                className="pl-7 h-7 w-full text-xs border-border/75"
              />
            </div>
          </div>
        </div>

        {/* Tabs — Figma: flex with thin dividers, active tab gets a
            bg-primary/10 wash + a 2px bottom underline bar. */}
        <div className="sticky top-0 z-10 flex items-center gap-0 border-b border-border/50 bg-background/15 backdrop-blur-md">
          {TABS.map((tab, idx) => (
            <Fragment key={tab}>
              {idx > 0 && <div className="w-px h-4 bg-border/75 shrink-0" />}
              <button
                type="button"
                onClick={() => {
                  setActiveTab(tab);
                  setSearch('');
                }}
                className={cn(
                  'flex-1 relative px-3 py-2 text-[14px] font-medium transition-all flex items-center justify-center gap-1.5',
                  activeTab === tab
                    ? 'text-foreground bg-primary/10'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {TAB_LABELS[tab]}
                <span
                  className={cn(
                    'text-[12px]',
                    activeTab === tab ? 'opacity-80' : 'opacity-50',
                  )}
                >
                  ({tabCounts[tab]})
                </span>
                {activeTab === tab && (
                  <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-primary rounded-t-full" />
                )}
              </button>
            </Fragment>
          ))}
        </div>

        {/* List */}
        <div className="max-h-72 overflow-y-auto divide-y divide-border/40">
          {noSearchMatches && (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No results found.
            </p>
          )}
          {(searching
            ? filteredRegions.length > 0
            : activeTab === 'regions') && (
            <>
              {filteredRegions.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  No results found.
                </p>
              ) : (
                filteredRegions.map((region) => {
                  const allExcluded = region.countryCodes.every((c) =>
                    excludedCountries.has(c),
                  );
                  const someExcluded = region.countryCodes.some((c) =>
                    excludedCountries.has(c),
                  );
                  const isPartial = someExcluded && !allExcluded;
                  const regionDisabled =
                    region.countryCodes.length > 0 &&
                    region.countryCodes.every((c) =>
                      countriesNotInUniverse?.has(c),
                    );
                  const excludedInRegion = region.countryCodes.filter((c) =>
                    excludedCountries.has(c),
                  ).length;
                  return (
                    <button
                      key={region.code}
                      type="button"
                      onClick={() => handleRegionToggle(region.countryCodes)}
                      className={cn(
                        'group flex items-center justify-between w-full px-4 py-2.5 text-left transition-colors',
                        regionDisabled
                          ? 'bg-muted/20 opacity-60 cursor-not-allowed'
                          : allExcluded
                            ? 'bg-destructive/[0.08] hover:bg-destructive/[0.12]'
                            : isPartial
                              ? 'bg-amber-500/5 hover:bg-amber-500/10'
                              : 'hover:bg-accent/50',
                      )}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <Badge
                          variant="outline"
                          className="font-mono text-xs shrink-0 tabular-nums"
                        >
                          {region.code}
                        </Badge>
                        <ClassificationTypeBadge
                          label="Region"
                          isLive={region.countryCodes.some((c) =>
                            liveCountries?.has(c),
                          )}
                        />
                        <div className="flex-1 min-w-0">
                          <div
                            className={cn(
                              'text-sm truncate',
                              allExcluded
                                ? 'text-destructive/80'
                                : isPartial
                                  ? 'text-amber-600/80'
                                  : '',
                            )}
                          >
                            {region.label}
                          </div>
                          <div className="text-xs text-muted-foreground truncate">
                            {region.countryCodes.length} countries available
                            {isPartial ? ` · ${excludedInRegion} excluded` : ''}
                          </div>
                        </div>
                      </div>
                      <div className="ml-3 shrink-0">
                        {allExcluded ? (
                          <Badge
                            variant="outline"
                            className="text-xs px-2 py-0.5 bg-destructive/10 text-destructive border-destructive/30"
                          >
                            Excluded
                          </Badge>
                        ) : isPartial ? (
                          <Badge
                            variant="outline"
                            className="text-xs px-2 py-0.5 bg-amber-500/10 text-amber-600 border-amber-500/30"
                          >
                            Partial
                          </Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground opacity-0 group-hover:opacity-60 transition-opacity flex items-center gap-1">
                            <Ban className="w-3 h-3" /> Exclude
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </>
          )}

          {(searching
            ? filteredCountries.length > 0
            : activeTab === 'countries') && (
            <>
              {filteredCountries.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  No results found.
                </p>
              ) : (
                filteredCountries.map((country) => {
                  const isExcluded = excludedCountries.has(country.code);
                  // Partial — some of the country's exchanges are excluded but
                  // the country itself isn't (a GICS-style partial parent).
                  const isPartial =
                    !isExcluded &&
                    visibleExchanges.some(
                      (e) =>
                        e.countryCode === country.code &&
                        excludedExchanges.has(e.code),
                    );
                  const isDisabled =
                    countriesNotInUniverse?.has(country.code) ?? false;
                  const regionLabel =
                    GEOGRAPHIC_REGIONS.find(
                      (r) => r.code === getRegionForCountry(country.code),
                    )?.label ?? '';
                  return (
                    <button
                      key={country.code}
                      type="button"
                      onClick={() => handleCountryToggle(country.code)}
                      className={cn(
                        'group flex items-center justify-between w-full px-4 py-2.5 text-left transition-colors',
                        isDisabled
                          ? 'bg-muted/20 opacity-60 cursor-not-allowed'
                          : isExcluded
                            ? 'bg-destructive/[0.08] hover:bg-destructive/[0.12]'
                            : isPartial
                              ? 'bg-amber-500/5 hover:bg-amber-500/10'
                              : 'hover:bg-accent/50',
                      )}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <Badge
                          variant="outline"
                          className="font-mono text-xs shrink-0 tabular-nums"
                        >
                          {country.code}
                        </Badge>
                        <ClassificationTypeBadge
                          label="Country"
                          isLive={liveCountries?.has(country.code) ?? false}
                        />
                        <div className="flex-1 min-w-0">
                          <div
                            className={cn(
                              'text-sm truncate',
                              isExcluded
                                ? 'text-destructive/80'
                                : isPartial
                                  ? 'text-amber-600/80'
                                  : '',
                            )}
                          >
                            {country.label}
                          </div>
                          {regionLabel && (
                            <div className="text-xs text-muted-foreground truncate">
                              {regionLabel}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="ml-3 shrink-0">
                        {isExcluded ? (
                          <Badge
                            variant="outline"
                            className="text-xs px-2 py-0.5 bg-destructive/10 text-destructive border-destructive/30"
                          >
                            Excluded
                          </Badge>
                        ) : isPartial ? (
                          <Badge
                            variant="outline"
                            className="text-xs px-2 py-0.5 bg-amber-500/10 text-amber-600 border-amber-500/30"
                          >
                            Partial
                          </Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground opacity-0 group-hover:opacity-60 transition-opacity flex items-center gap-1">
                            <Ban className="w-3 h-3" /> Exclude
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </>
          )}

          {(searching
            ? filteredExchanges.length > 0
            : activeTab === 'exchanges') && (
            <>
              {filteredExchanges.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  No results found.
                </p>
              ) : (
                filteredExchanges.map((exchange) => {
                  // Excluded explicitly, or implied because its parent country
                  // is excluded — either way it's toggleable: toggling an
                  // implied one back demotes the country to "partial" (mirrors a
                  // GICS sub-industry). Only 0-securities (live data) disables it.
                  const isExcluded =
                    excludedExchanges.has(exchange.code) ||
                    excludedCountries.has(exchange.countryCode);
                  const isDisabled =
                    countriesNotInUniverse?.has(exchange.countryCode) ?? false;
                  const countryLabel =
                    countryLabelByCode.get(exchange.countryCode) ??
                    exchange.countryCode;
                  // Full path: Region › Country.
                  const exchangeRegionLabel = GEOGRAPHIC_REGIONS.find(
                    (r) => r.code === getRegionForCountry(exchange.countryCode),
                  )?.label;
                  return (
                    <button
                      key={exchange.code}
                      type="button"
                      onClick={() => onToggleExchange(exchange.code)}
                      className={cn(
                        'group flex items-center justify-between w-full px-4 py-2.5 text-left transition-colors',
                        isDisabled
                          ? 'bg-muted/20 opacity-60 cursor-not-allowed'
                          : isExcluded
                            ? 'bg-destructive/[0.08] hover:bg-destructive/[0.12]'
                            : 'hover:bg-accent/50',
                      )}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <Badge
                          variant="outline"
                          className="font-mono text-xs shrink-0 tabular-nums"
                        >
                          {exchange.code}
                        </Badge>
                        <ClassificationTypeBadge
                          label="Exchange"
                          isLive={liveMics?.has(exchange.code) ?? false}
                        />
                        <div className="flex-1 min-w-0">
                          <div
                            className={cn(
                              'text-sm truncate',
                              isExcluded ? 'text-destructive/80' : '',
                            )}
                            // The official name is one hover away; the short
                            // form is what the row is scanned for.
                            title={exchange.label}
                          >
                            {exchangeShortName(exchange.code)}
                          </div>
                          <div className="text-xs text-muted-foreground truncate">
                            {exchangeRegionLabel
                              ? `${exchangeRegionLabel} • ${countryLabel}`
                              : countryLabel}
                          </div>
                        </div>
                      </div>
                      <div className="ml-3 shrink-0">
                        {isExcluded ? (
                          <Badge
                            variant="outline"
                            className="text-xs px-2 py-0.5 bg-destructive/10 text-destructive border-destructive/30"
                          >
                            Excluded
                          </Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground opacity-0 group-hover:opacity-60 transition-opacity flex items-center gap-1">
                            <Ban className="w-3 h-3" /> Exclude
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
